"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getComparisonDateRange = exports.getComparisonPeriod = exports.RESTRICTED_CHART_INTERVALS = exports.getEquivalentTimezone = exports.convertDateToTimezone = exports.convertDate = void 0;
const tslib_1 = require("tslib");
const dateTime_1 = require("../typedef/dateTime");
const moment_timezone_1 = tslib_1.__importDefault(require("moment-timezone"));
const countryUtils_1 = require("./countryUtils");
const convertDate = (fromDate, toDate, countryCode, timezoneSelectedByUser) => {
    const chosenTz = (0, exports.getEquivalentTimezone)(countryCode);
    if (chosenTz) {
        const fromDateMoment = moment_timezone_1.default
            .unix(fromDate)
            .tz(timezoneSelectedByUser)
            .startOf("d")
            .add(12, "h")
            .tz(chosenTz === null || chosenTz === void 0 ? void 0 : chosenTz.name, true);
        const toDateMoment = moment_timezone_1.default
            .unix(toDate)
            .tz(timezoneSelectedByUser)
            .startOf("d")
            .add(12, "h")
            .tz(chosenTz === null || chosenTz === void 0 ? void 0 : chosenTz.name, true);
        const daysDifference = toDateMoment.diff(fromDateMoment, "days") + 1;
        return { fromDateMoment, toDateMoment, daysDifference, chosenTz };
    }
    else {
        throw new Error(`No valid timezone`);
    }
};
exports.convertDate = convertDate;
const convertDateToTimezone = (date, timezoneSelectedByUser, chosenTz) => {
    return moment_timezone_1.default
        .unix(date)
        .tz(timezoneSelectedByUser)
        .startOf("d")
        .add(12, "h")
        .tz(chosenTz === null || chosenTz === void 0 ? void 0 : chosenTz.name, true);
};
exports.convertDateToTimezone = convertDateToTimezone;
const getEquivalentTimezone = (countryCode) => {
    const ISOCountryCode = (0, countryUtils_1.countryCodeToISO2)(countryCode);
    const sortedArray = moment_timezone_1.default.tz
        .zonesForCountry(ISOCountryCode)
        .map((z) => moment_timezone_1.default.tz.zone(z))
        .filter((z) => Boolean(z))
        .map((z) => {
        return z.utcOffset((0, moment_timezone_1.default)("2023-01-01T00:00:00Z").valueOf());
    })
        .sort((a, b) => {
        return a - b;
    });
    const median = sortedArray[Math.floor(sortedArray.length / 2)];
    const offset = median && Math.round(median / 60) * 60;
    const etcTimezones = moment_timezone_1.default.tz
        .names()
        .filter((n) => n.includes("Etc/"))
        .map((n) => moment_timezone_1.default.tz.zone(n));
    const chosenTz = etcTimezones.find((t) => (t === null || t === void 0 ? void 0 : t.offsets[0]) === offset);
    return chosenTz;
};
exports.getEquivalentTimezone = getEquivalentTimezone;
exports.RESTRICTED_CHART_INTERVALS = [
    dateTime_1.INTERVAL.MONTHS,
    dateTime_1.INTERVAL.QUARTERS,
    dateTime_1.INTERVAL.YEARS,
];
const getComparisonPeriod = (date, currentCompare, diff, interval) => {
    if (currentCompare === "LASTYEAR") {
        return date.clone().subtract(1, "years").unix();
    }
    else if (currentCompare === "PREVIOUSFISCALPERIOD") {
        const currentYear = date.isoWeekYear();
        const timezone = date.tz() || moment_timezone_1.default.tz.guess();
        const previousFiscalPeriod = (0, moment_timezone_1.default)("2024-06-01")
            .tz(timezone)
            .set("year", currentYear - 1)
            .set("isoWeekday", date.isoWeekday())
            .set("isoWeek", date.isoWeek())
            .set("hours", date.hours())
            .set("minutes", date.minutes())
            .set("seconds", date.seconds())
            .set("milliseconds", date.milliseconds());
        return previousFiscalPeriod.unix();
    }
    else {
        return date.clone().subtract(diff, interval).unix();
    }
};
exports.getComparisonPeriod = getComparisonPeriod;
const getComparisonDateRange = (fromDateMoment, toDateMoment, currentPeriod, currentCompare, timezone) => {
    switch (currentPeriod) {
        case dateTime_1.DATETIME_PERIODS.DAY:
        case dateTime_1.DATETIME_PERIODS.YESTERDAY:
            return {
                priorFromDate: (0, exports.getComparisonPeriod)(fromDateMoment, currentCompare, 1, dateTime_1.INTERVAL.DAYS),
                priorToDate: (0, exports.getComparisonPeriod)(toDateMoment, currentCompare, 1, dateTime_1.INTERVAL.DAYS),
            };
        case dateTime_1.DATETIME_PERIODS.WEEK:
        case dateTime_1.DATETIME_PERIODS.WEEKMTS:
            return {
                priorFromDate: (0, exports.getComparisonPeriod)(fromDateMoment, currentCompare, 1, dateTime_1.INTERVAL.WEEKS),
                priorToDate: (0, exports.getComparisonPeriod)(toDateMoment, currentCompare, 1, dateTime_1.INTERVAL.WEEKS),
            };
        case dateTime_1.DATETIME_PERIODS.LAST30:
            return {
                priorFromDate: (0, exports.getComparisonPeriod)(fromDateMoment, currentCompare, 30, dateTime_1.INTERVAL.DAYS),
                priorToDate: (0, exports.getComparisonPeriod)(toDateMoment, currentCompare, 30, dateTime_1.INTERVAL.DAYS),
            };
        case dateTime_1.DATETIME_PERIODS.MTD:
            const subtract = currentCompare === "LASTYEAR" ? "year" : "month";
            return {
                priorFromDate: fromDateMoment
                    .clone()
                    .tz(timezone)
                    .subtract(1, subtract)
                    .startOf("month")
                    .unix(),
                priorToDate: toDateMoment
                    .clone()
                    .tz(timezone)
                    .endOf("day")
                    .subtract(1, subtract)
                    .unix(),
            };
        case dateTime_1.DATETIME_PERIODS.CURRENTMONTH:
        case dateTime_1.DATETIME_PERIODS.LASTMONTH:
            return {
                priorFromDate: (0, exports.getComparisonPeriod)(fromDateMoment, currentCompare, 1, dateTime_1.INTERVAL.MONTHS),
                priorToDate: (0, exports.getComparisonPeriod)(toDateMoment, currentCompare, 1, dateTime_1.INTERVAL.MONTHS),
            };
        case dateTime_1.DATETIME_PERIODS.CURRENTQ:
        case dateTime_1.DATETIME_PERIODS.LASTQ:
            return {
                priorFromDate: (0, exports.getComparisonPeriod)(fromDateMoment, currentCompare, 1, dateTime_1.INTERVAL.QUARTERS),
                priorToDate: (0, exports.getComparisonPeriod)(toDateMoment, currentCompare, 1, dateTime_1.INTERVAL.QUARTERS),
            };
        case dateTime_1.DATETIME_PERIODS.LAST12:
            return {
                priorFromDate: (0, exports.getComparisonPeriod)(fromDateMoment, currentCompare, 12, dateTime_1.INTERVAL.MONTHS),
                priorToDate: (0, exports.getComparisonPeriod)(toDateMoment, currentCompare, 12, dateTime_1.INTERVAL.MONTHS),
            };
        case dateTime_1.DATETIME_PERIODS.YEAR:
        case dateTime_1.DATETIME_PERIODS.CURRENTYEAR:
            return {
                priorFromDate: (0, exports.getComparisonPeriod)(fromDateMoment, currentCompare, 1, dateTime_1.INTERVAL.YEARS),
                priorToDate: (0, exports.getComparisonPeriod)(toDateMoment, currentCompare, 1, dateTime_1.INTERVAL.YEARS),
            };
        default:
            return {
                priorFromDate: fromDateMoment.unix(),
                priorToDate: toDateMoment.unix(),
            };
    }
};
exports.getComparisonDateRange = getComparisonDateRange;
//# sourceMappingURL=dateTimeUtils.js.map